import { Link } from "react-router-dom";
import { ArrowRight, Ear, Users, Eye, Compass } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Motion";
import Bueno from "@/components/site/Bueno";

const IMG = {
  child: "/photos/bueno-jardin.webp",
  hero: "/photos/bueno-jardin-assis.webp",
};

const STEPS = [
  {
    num: "01",
    icon: Ear,
    title: "L'écoute",
    text: "Un premier échange téléphonique ou par message, sans engagement, pour comprendre votre situation et répondre à vos questions.",
  },
  {
    num: "02",
    icon: Users,
    title: "La rencontre",
    text: "Je viens à votre domicile rencontrer toute la famille — enfants, parents et animaux. Chacun est accueilli tel qu'il est.",
  },
  {
    num: "03",
    icon: Eye,
    title: "L'observation",
    text: "J'observe la dynamique familiale, je détermine avec vous vos besoins et le rôle de chacun dans votre quotidien, là où les problématiques se manifestent réellement.",
  },
  {
    num: "04",
    icon: Compass,
    title: "La mise en place",
    text: "Nous construisons ensemble un plan sur mesure alliant médiation animale, accompagnement parental et éducation canine, avec un suivi dans la durée.",
  },
];

export default function Methode() {
  return (
    <div data-testid="methode-page">
      <PageHero
        testId="methode-hero"
        overline="La méthode"
        lines={[
          { text: "Une approche en" },
          { text: "trois temps," },
          { text: "un seul objectif.", accent: true },
        ]}
        intro="Ni thérapie classique, ni simple dressage : une méthode de terrain, pensée pour votre famille et vécue chez vous, étape par étape."
      />

      {/* BENTO STEPS */}
      <section className="px-6 pb-24 sm:px-10 md:pb-32 lg:px-16" data-testid="methode-steps">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7" y={40}>
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_60px_-30px_rgba(43,58,48,0.4)]">
              <img src={IMG.child} alt="Enfants jouant avec Bueno dans le jardin" loading="lazy" style={{ objectPosition: "50% 65%" }} className="aspect-[16/9] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1} y={40}>
            <div className="flex h-full flex-col justify-center rounded-[2rem] bg-terracotta p-10 text-cream md:p-12" data-testid="methode-step-01">
              <span className="font-serif text-6xl italic text-cream/30">{STEPS[0].num}</span>
              <h3 className="mt-4 font-serif text-3xl font-medium">{STEPS[0].title}</h3>
              <p className="mt-4 text-base leading-relaxed text-cream/85">{STEPS[0].text}</p>
            </div>
          </Reveal>

          {STEPS.slice(1).map((step, i) => (
            <Reveal key={step.num} className="md:col-span-4" delay={0.08 * i} y={40}>
              <div
                className={`flex h-full flex-col rounded-[2rem] border p-10 transition-transform duration-300 hover:-translate-y-1.5 ${
                  i === 1 ? "border-transparent bg-forest text-cream" : "border-stone-border bg-white/60"
                }`}
                data-testid={`methode-step-${step.num}`}
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-full ${i === 1 ? "bg-cream/15 text-sand" : "bg-terracotta/10 text-terracotta"}`}>
                  <step.icon size={20} strokeWidth={1.8} />
                </span>
                <span className={`mt-6 font-serif text-5xl italic leading-none ${i === 1 ? "text-cream/25" : "text-terracotta/25"}`}>
                  {step.num}
                </span>
                <h3 className={`mt-3 font-serif text-2xl font-medium ${i === 1 ? "text-cream" : "text-ink"}`}>{step.title}</h3>
                <p className={`mt-4 text-sm leading-relaxed ${i === 1 ? "text-cream/80" : "text-ink/65"}`}>{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POURQUOI L'ANIMAL */}
      <section className="bg-linen px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="methode-pourquoi">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline-label">Pourquoi l'animal ?</p>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
                Parce qu'il ne juge pas. <span className="italic text-terracotta">Il relie.</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ink/70 md:text-lg">
                L'animal désamorce les tensions, fait baisser les gardes et crée un
                langage commun là où les mots ne suffisent plus. C'est ce levier
                unique que ma méthode met au service de votre famille. Bueno, mon
                pomsky fauve né en janvier 2026, se forme d'ailleurs au rôle de
                chien médiateur — et il est déjà formidable auprès des enfants.
              </p>
              <Link to="/contact" className="btn-primary mt-9" data-testid="methode-cta-button">
                En parler ensemble <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="relative lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1} y={50}>
              <div className="relative">
                <div className="absolute -left-5 -top-5 h-full w-full blob-2 bg-sand/50" aria-hidden="true" />
                <div className="blob-2 relative aspect-[4/5] overflow-hidden shadow-[0_30px_60px_-30px_rgba(43,58,48,0.4)]">
                  <img src={IMG.hero} alt="Bueno assis dans le jardin devant la haie" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" style={{ objectPosition: "50% 42%" }} />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Bueno bg="bg-cream" />
    </div>
  );
}
